import { Controller } from "@hotwired/stimulus"
import { Sortable } from "sortablejs"

// Dashboard tab row: keeps the active tab visible in the horizontally scrolling row, fades the
// edge that has more tabs behind it (the scrollbar itself is hidden), lets a plain mouse wheel
// scroll the row sideways, and opens/closes the "Neues Dashboard" and "Umbenennen" dialogs.
// Tabs can be dragged into a new order (saved right away, rolled back if that fails), and the
// active tab is renamed in place by double-click — through the rename dialog's own form, so
// both ways share one request and one validation.
export default class extends Controller {
  static targets = ["scroller", "active", "fadeStart", "fadeEnd", "tab"]
  static values = { reorderUrl: String, errorMessage: String }

  connect() {
    this.initializeSortable()
    if (this.hasActiveTarget && this.hasScrollerTarget) {
      const tab = this.activeTarget
      this.scrollerTarget.scrollLeft = tab.offsetLeft - this.scrollerTarget.offsetLeft - 40
    }
    this.updateFades()
    this.resizeObserver = new ResizeObserver(() => this.updateFades())
    if (this.hasScrollerTarget) this.resizeObserver.observe(this.scrollerTarget)
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.sortable?.destroy()
  }

  // -- reorder -------------------------------------------------------------

  initializeSortable() {
    if (!this.hasScrollerTarget || !this.reorderUrlValue) return

    this.sortable = new Sortable(this.scrollerTarget, {
      draggable: '[data-dashboard-tabs-target~="tab"]',
      direction: "horizontal",
      animation: 150,
      delay: 200,
      delayOnTouchOnly: true,
      ghostClass: "opacity-40",
      onStart: () => {
        this.previousOrder = this.tabOrder()
      },
      onEnd: (event) => {
        if (event.oldIndex !== event.newIndex) this.saveOrder()
      },
    })
  }

  tabOrder() {
    return this.tabTargets.map((tab) => tab.dataset.dashboardId)
  }

  async saveOrder() {
    try {
      const response = await fetch(this.reorderUrlValue, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": document.head.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json",
          "Content-type": "application/json",
        },
        body: JSON.stringify({ ordered_ids: this.tabOrder() }),
      })
      if (!response.ok) throw new Error("Tab order not saved")
    } catch {
      this.restoreOrder()
    }
  }

  restoreOrder() {
    const byId = Object.fromEntries(this.tabTargets.map((tab) => [tab.dataset.dashboardId, tab]))
    this.previousOrder?.forEach((id) => this.scrollerTarget.appendChild(byId[id]))
    this.showErrorToast()
  }

  showErrorToast() {
    if (!this.errorMessageValue) return

    const toast = document.createElement("div")
    toast.className =
      "fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-4 py-3 text-sm text-white shadow-lg"
    toast.setAttribute("role", "status")
    toast.textContent = this.errorMessageValue
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 4000)
  }

  // -- rename in place -----------------------------------------------------

  // A click on the tab already shown only reloaded the page; now it is the first half of the
  // double-click that renames it. Cmd/Ctrl/Shift-clicks and middle clicks still open it in a new
  // tab or window, as on every other tab.
  keepActive(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return

    event.preventDefault()
  }

  startRename(event) {
    event.preventDefault()
    const tab = event.currentTarget
    if (this.renameInput) return

    const input = document.createElement("input")
    input.type = "text"
    input.maxLength = 60
    input.value = tab.textContent.trim()
    input.setAttribute("aria-label", tab.getAttribute("title") || tab.textContent.trim())
    input.className =
      "my-1.5 w-40 rounded-md border-gray-300 px-2 py-1 text-sm focus:border-bautask-green focus:ring-bautask-green"
    input.addEventListener("keydown", (keyEvent) => {
      if (keyEvent.key === "Enter") {
        keyEvent.preventDefault()
        this.commitRename()
      } else if (keyEvent.key === "Escape") {
        keyEvent.preventDefault()
        this.cancelRename()
      }
    })
    input.addEventListener("blur", () => this.commitRename())

    this.renameTab = tab
    this.renameInput = input
    tab.hidden = true
    tab.after(input)
    input.focus()
    input.select()
  }

  commitRename() {
    if (!this.renameInput) return

    const name = this.renameInput.value.trim()
    const form = this.element.querySelector("#dashboard-rename-dialog form")
    const field = form?.querySelector('input[name="dashboard_configuration[name]"]')
    if (!name || name === this.renameTab.textContent.trim() || !field) {
      this.cancelRename()
      return
    }

    field.value = name
    this.renameTab.textContent = name
    this.cancelRename()
    form.requestSubmit()
  }

  cancelRename() {
    if (!this.renameInput) return

    const input = this.renameInput
    this.renameInput = null
    this.renameTab.hidden = false
    input.remove()
  }

  updateFades() {
    if (!this.hasScrollerTarget) return

    const { scrollLeft, scrollWidth, clientWidth } = this.scrollerTarget
    this.toggleFade(this.fadeStartTargets, scrollLeft > 1)
    this.toggleFade(this.fadeEndTargets, scrollLeft + clientWidth < scrollWidth - 1)
  }

  // Trackpads already scroll sideways; a mouse wheel only sends deltaY.
  scrollHorizontally(event) {
    const scroller = this.scrollerTarget
    if (scroller.scrollWidth <= scroller.clientWidth) return
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return

    event.preventDefault()
    scroller.scrollLeft += event.deltaY
  }

  openDialog(event) {
    const dialog = document.getElementById(event.currentTarget.dataset.dialogId)
    if (!dialog) return

    dialog.showModal()
    dialog.querySelector("input[type=text]")?.select()
  }

  closeDialog(event) {
    event.currentTarget.closest("dialog")?.close()
  }

  toggleFade(targets, visible) {
    targets.forEach((fade) => fade.classList.toggle("opacity-0", !visible))
  }
}
